import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AppBar } from "../../components/AppShell";
import { useToast } from "../../components/Toast";
import { request } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { coverFor, fmtDuration } from "../../lib/helpers";
import type { CourseListItem } from "../../lib/types";

const BANNERS: Record<string, { color: string; bg: string; icon: string; label: string }> = {
  medic: { color: "#009ab0", bg: "var(--c-primary-light)", icon: "fa-user-doctor", label: "Medici" },
  rezident: { color: "#8a5a04", bg: "#fdf1de", icon: "fa-stethoscope", label: "Rezidenți" },
  asistent: { color: "#4a2c91", bg: "#ede4fa", icon: "fa-hand-holding-medical", label: "Asistenți" },
};

export function Catalog() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [category, setCategory] = useState<string | null>(null);

  const courses = useQuery({ queryKey: ["courses"], queryFn: () => request<CourseListItem[]>("/courses") });
  const enrolled = useQuery({ queryKey: ["me", "enrollments"], queryFn: () => request<CourseListItem[]>("/enrollments/me") });

  const enrolledIds = useMemo(() => new Set((enrolled.data || []).map((c) => c.id)), [enrolled.data]);
  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    (courses.data || []).forEach((c) => counts.set(c.category, (counts.get(c.category) || 0) + 1));
    return Array.from(counts.entries()).sort();
  }, [courses.data]);

  const filtered = (courses.data || []).filter((c) => !category || c.category === category);

  const enroll = useMutation({
    mutationFn: (id: string) => request(`/enrollments/courses/${id}`, { method: "POST" }),
    onSuccess: () => {
      toast("Te-ai înscris cu succes!", "success");
      qc.invalidateQueries({ queryKey: ["me", "enrollments"] });
    },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  const banner = user ? BANNERS[user.role] : null;

  return (
    <>
      <AppBar crumbs={[{ label: "Tabloul meu", to: "/student/dashboard" }, { label: "Catalog cursuri" }]} />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Catalog cursuri</h1>
            <p>Cursurile disponibile pentru rolul tău.</p>
          </div>
        </div>

        {banner && (
          <div className="card" style={{ marginBottom: 24, background: `linear-gradient(135deg, ${banner.bg}, #fff)`, borderColor: banner.bg }}>
            <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: banner.bg, color: banner.color, display: "grid", placeItems: "center", fontSize: "1.3rem" }}>
                <i className={`fa-solid ${banner.icon}`}></i>
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <strong style={{ display: "block", fontSize: "1rem", color: banner.color }}>Cursuri pentru {banner.label}</strong>
                <small style={{ color: banner.color, opacity: 0.85 }}>Conținut adaptat rolului tău.</small>
              </div>
            </div>
          </div>
        )}

        <div className="tabs">
          <div className={"tab" + (category === null ? " active" : "")} onClick={() => setCategory(null)}>Toate ({courses.data?.length ?? 0})</div>
          {categories.map(([cat, n]) => (
            <div key={cat} className={"tab" + (category === cat ? " active" : "")} onClick={() => setCategory(cat)}>{cat} ({n})</div>
          ))}
        </div>

        {courses.isLoading ? (
          <div className="card text-muted">Se încarcă…</div>
        ) : filtered.length === 0 ? (
          <div className="card text-muted">Nu sunt cursuri în această categorie.</div>
        ) : (
          <div className="grid-3">
            {filtered.map((c) => {
              const isEnrolled = enrolledIds.has(c.id);
              return (
                <article key={c.id} className="course-card">
                  <div className="course-cover" style={{ background: coverFor(c) }}>
                    {isEnrolled && <span className="tag">Înscris</span>}
                    <div><div className="module">{c.category}</div><strong>{c.title}</strong></div>
                  </div>
                  <div className="course-body">
                    <p>{c.subtitle || ""}</p>
                    <div className="course-meta">
                      <span><i className="fa-regular fa-clock"></i> {fmtDuration(c.duration_minutes)}</span>
                      {c.instructor && <span><i className="fa-solid fa-user-doctor"></i> {c.instructor.full_name}</span>}
                    </div>
                    {isEnrolled ? (
                      <Link to={`/student/curs/${c.id}`} className="btn btn-primary btn-sm btn-block">Deschide cursul</Link>
                    ) : (
                      <button
                        className="btn btn-primary btn-sm btn-block"
                        disabled={enroll.isPending}
                        onClick={() => enroll.mutate(c.id)}
                      >
                        {enroll.isPending ? "Se înscrie..." : "Înscrie-te"}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
